<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ isEdit ? $t('edit_discount_campaign') : $t('add_discount_campaign') }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/discount/campaigns" class="btn btn-added"><span>Back</span></router-link>
          </div>
        </li>
      </ul>
    </div>

    <form @submit.prevent="submitForm">
      <div class="card mb-3">
        <div class="card-body">
          <h5 class="mb-3">{{ $t('basic_information') }}</h5>
          <div class="row">
            <div class="col-lg-6 mb-3">
              <label class="form-label">{{ $t('campaign_name') }} <span class="text-danger">*</span></label>
              <input v-model="form.name" type="text" class="form-control" required />
              <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div>
            </div>
            <div class="col-lg-3 mb-3">
              <label class="form-label">{{ $t('priority') }}</label>
              <input v-model.number="form.priority" type="number" class="form-control" />
            </div>
            <div class="col-lg-3 mb-3">
              <label class="form-label">{{ $t('status') }}</label>
              <input :value="statusLabel(form.effective_status || form.status || 'draft')" class="form-control" disabled />
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">{{ $t('description') }}</label>
              <textarea v-model="form.description" class="form-control" rows="2"></textarea>
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">{{ $t('store_selection') }}</label>
              <div class="border rounded p-2" style="max-height: 160px; overflow: auto;">
                <div class="form-check mb-2">
                  <input
                    id="all-stores"
                    v-model="allStores"
                    class="form-check-input"
                    type="checkbox"
                    @change="onAllStoresChange"
                  />
                  <label class="form-check-label" for="all-stores">{{ $t('all_authorized_stores') }}</label>
                </div>
                <div v-for="store in stores" :key="store.id" class="form-check">
                  <input
                    :id="`store-${store.id}`"
                    v-model="form.store_ids"
                    class="form-check-input"
                    type="checkbox"
                    :value="store.id"
                    :disabled="allStores"
                  />
                  <label class="form-check-label" :for="`store-${store.id}`">{{ store.name }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <h5 class="mb-3">{{ $t('campaign_validity') }}</h5>
          <div class="row">
            <div class="col-md-3 mb-3">
              <label class="form-label">{{ $t('start_date') }} <span class="text-danger">*</span></label>
              <input v-model="startDate" type="date" class="form-control" required />
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">{{ $t('start_time') }} <span class="text-danger">*</span></label>
              <input v-model="startTime" type="time" class="form-control" required />
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">{{ $t('end_date') }} <span class="text-danger">*</span></label>
              <input v-model="endDate" type="date" class="form-control" required />
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">{{ $t('end_time') }} <span class="text-danger">*</span></label>
              <input v-model="endTime" type="time" class="form-control" required />
            </div>
            <div v-if="errors.non_field_errors" class="col-12 text-danger">{{ errors.non_field_errors }}</div>
            <div v-if="errors.start_at" class="col-12 text-danger">{{ errors.start_at }}</div>
            <div v-if="errors.end_at" class="col-12 text-danger">{{ errors.end_at }}</div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <h5 class="mb-3">{{ $t('discount_configuration') }}</h5>
          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label">{{ $t('discount_type') }} <span class="text-danger">*</span></label>
              <select v-model="form.discount_type" class="form-select" required>
                <option value="percentage">{{ $t('percentage_discount') }}</option>
                <option value="fixed_amount">{{ $t('fixed_amount_discount') }}</option>
                <option value="promotional_price">{{ $t('promotional_selling_price') }}</option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">{{ $t('discount_value') }} <span class="text-danger">*</span></label>
              <input v-model.number="form.discount_value" type="number" step="0.0001" min="0" class="form-control" required />
              <div v-if="errors.discount_value" class="text-danger mt-1">{{ errors.discount_value }}</div>
            </div>
            <div v-if="form.discount_type === 'percentage'" class="col-md-4 mb-3">
              <label class="form-label">{{ $t('max_discount_amount') }}</label>
              <input v-model.number="form.max_discount_amount" type="number" step="0.0001" min="0" class="form-control" />
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h5 class="mb-0">{{ $t('item_selection') }} ({{ selectedCount }})</h5>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-outline-primary" @click="selectAllFiltered">
                {{ $t('select_all_filtered') }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAll">
                {{ $t('deselect_all') }}
              </button>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-4 mb-2">
              <input
                v-model="itemSearch"
                type="search"
                class="form-control"
                :placeholder="$t('search_item_sku_barcode')"
                @input="onItemSearch"
              />
            </div>
            <div class="col-md-4 mb-2">
              <select v-model="categoryId" class="form-select" @change="loadAssignable(1)">
                <option value="">{{ $t('all_categories') }}</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.node || c.name }}</option>
              </select>
            </div>
            <div class="col-md-4 mb-2">
              <select v-model="subCategoryId" class="form-select" @change="loadAssignable(1)">
                <option value="">{{ $t('all_sub_categories') }}</option>
                <option v-for="c in subCategories" :key="c.id" :value="c.id">{{ c.node || c.name }}</option>
              </select>
            </div>
          </div>

          <div class="table-responsive mb-3" style="max-height: 280px; overflow: auto;">
            <table class="table table-sm table-hover">
              <thead class="thead-light sticky-top">
                <tr>
                  <th style="width: 40px;"></th>
                  <th>{{ $t('item_name') }}</th>
                  <th>{{ $t('sku') }} / {{ $t('barcode') }}</th>
                  <th>{{ $t('variant') }}</th>
                  <th>{{ $t('selling_price') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in assignableRows" :key="row.key">
                  <td>
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="isSelected(row.key)"
                      @change="toggleRow(row)"
                    />
                  </td>
                  <td>{{ row.item_name }}</td>
                  <td>{{ row.sku || row.barcode || '—' }}</td>
                  <td>{{ row.variant_label || '—' }}</td>
                  <td>{{ formatMoney(row.regular_selling_price) }}</td>
                </tr>
                <tr v-if="!assignableRows.length">
                  <td colspan="5" class="text-muted text-center">{{ $t('no_data_found') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-4">
            <span class="text-muted small">{{ assignableCount }} {{ $t('results') }}</span>
            <div class="btn-group">
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="assignablePage <= 1" @click="loadAssignable(assignablePage - 1)">‹</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" disabled>{{ assignablePage }}</button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="assignablePage * assignablePageSize >= assignableCount"
                @click="loadAssignable(assignablePage + 1)"
              >›</button>
            </div>
          </div>

          <div v-if="errors.item_assignments" class="text-danger mb-2">{{ errors.item_assignments }}</div>
          <h6 class="mb-2">{{ $t('selected_items') }}</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('item_name') }}</th>
                  <th>{{ $t('sku') }} / {{ $t('barcode') }}</th>
                  <th>{{ $t('variant') }}</th>
                  <th>{{ $t('selling_price') }}</th>
                  <th>{{ $t('discount') }}</th>
                  <th>{{ $t('promotional_price') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in selectedList" :key="row.key">
                  <td>{{ row.item_name }}</td>
                  <td>{{ row.sku || row.barcode || '—' }}</td>
                  <td>{{ row.variant_label || '—' }}</td>
                  <td>{{ formatMoney(row.regular_selling_price) }}</td>
                  <td>{{ formatLineDiscount(row) }}</td>
                  <td>{{ formatMoney(promoPrice(row)) }}</td>
                  <td>
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSelected(row.key)">
                      <i class="ti ti-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!selectedList.length">
                  <td colspan="7" class="text-muted text-center">{{ $t('no_items_selected') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="text-end mb-4">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ isEdit ? $t('update') : $t('save') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import apiUrls from '@/urls';

function pad(n) {
  return String(n).padStart(2, '0');
}

function splitDateTime(iso) {
  if (!iso) return { date: '', time: '00:00' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const [datePart, timePart = '00:00'] = String(iso).split(/[T ]/);
    return { date: datePart, time: timePart.slice(0, 5) };
  }
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export default {
  name: 'DiscountCampaignForm',
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      toast: useToast(),
      saving: false,
      errors: {},
      stores: [],
      categories: [],
      subCategories: [],
      allStores: true,
      startDate: '',
      startTime: '00:00',
      endDate: '',
      endTime: '23:59',
      itemSearch: '',
      searchTimer: null,
      categoryId: '',
      subCategoryId: '',
      assignableRows: [],
      assignablePage: 1,
      assignablePageSize: 20,
      assignableCount: 0,
      selectedMap: {},
      form: {
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_discount_amount: null,
        priority: 0,
        store_ids: [],
        status: 'draft',
        effective_status: 'draft',
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.id);
    },
    selectedList() {
      return Object.values(this.selectedMap);
    },
    selectedCount() {
      return this.selectedList.length;
    },
  },
  async mounted() {
    await Promise.all([this.loadStores(), this.loadCategories()]);
    if (this.isEdit) {
      await this.loadCampaign();
    } else {
      const today = new Date();
      this.startDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      this.endDate = this.startDate;
    }
    await this.loadAssignable(1);
  },
  methods: {
    statusLabel(status) {
      const map = {
        draft: this.$t('status_draft'),
        scheduled: this.$t('status_scheduled'),
        active: this.$t('status_active'),
        paused: this.$t('status_paused'),
        expired: this.$t('status_expired'),
        cancelled: this.$t('status_cancelled'),
      };
      return map[status] || status;
    },
    formatMoney(v) {
      return `৳${Number(v || 0).toFixed(2)}`;
    },
    formatLineDiscount(row) {
      const price = Number(row.regular_selling_price || 0);
      if (this.form.discount_type === 'percentage') return `${Number(this.form.discount_value || 0)}%`;
      if (this.form.discount_type === 'fixed_amount') return this.formatMoney(this.form.discount_value);
      const promo = Number(this.form.discount_value || 0);
      return this.formatMoney(Math.max(price - promo, 0));
    },
    promoPrice(row) {
      const price = Number(row.regular_selling_price || 0);
      if (this.form.discount_type === 'promotional_price') {
        return Number(this.form.discount_value || 0);
      }
      if (this.form.discount_type === 'percentage') {
        let disc = (price * Number(this.form.discount_value || 0)) / 100;
        if (this.form.max_discount_amount != null && this.form.max_discount_amount !== '') {
          disc = Math.min(disc, Number(this.form.max_discount_amount));
        }
        return Math.max(price - disc, 0);
      }
      return Math.max(price - Number(this.form.discount_value || 0), 0);
    },
    async loadStores() {
      const { data } = await this.$axios.get(apiUrls.stores.get);
      this.stores = Array.isArray(data) ? data : (data.results || data.data || []);
    },
    async loadCategories() {
      try {
        const [cats, subs] = await Promise.all([
          this.$axios.get(apiUrls.categories.get),
          this.$axios.get(apiUrls.sub_categories.get),
        ]);
        this.categories = Array.isArray(cats.data) ? cats.data : (cats.data?.data || []);
        this.subCategories = Array.isArray(subs.data) ? subs.data : (subs.data?.data || []);
      } catch (_) {
        this.categories = [];
        this.subCategories = [];
      }
    },
    async loadCampaign() {
      const { data } = await this.$axios.get(apiUrls.discount_campaign.detail(this.id));
      this.form = {
        name: data.name,
        description: data.description || '',
        discount_type: data.discount_type,
        discount_value: Number(data.discount_value),
        max_discount_amount: data.max_discount_amount != null ? Number(data.max_discount_amount) : null,
        priority: data.priority || 0,
        store_ids: (data.stores || []).map((s) => s.id),
        status: data.status,
        effective_status: data.effective_status,
      };
      this.allStores = !(data.stores && data.stores.length);
      const start = splitDateTime(data.start_at);
      const end = splitDateTime(data.end_at);
      this.startDate = start.date;
      this.startTime = start.time;
      this.endDate = end.date;
      this.endTime = end.time;
      const map = {};
      (data.items || []).forEach((item) => {
        const key = `${item.item}:${item.variant || ''}`;
        map[key] = {
          key,
          item_id: item.item,
          variant_id: item.variant,
          item_name: item.item_name,
          sku: item.sku,
          barcode: item.barcode,
          variant_label: item.variant_label,
          regular_selling_price: item.regular_selling_price,
        };
      });
      this.selectedMap = map;
    },
    onAllStoresChange() {
      if (this.allStores) this.form.store_ids = [];
    },
    onItemSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadAssignable(1), 300);
    },
    async loadAssignable(page) {
      this.assignablePage = page;
      const params = {
        page,
        page_size: this.assignablePageSize,
        q: this.itemSearch || undefined,
        category: this.categoryId || undefined,
        sub_category: this.subCategoryId || undefined,
      };
      const { data } = await this.$axios.get(apiUrls.discount_campaign.assignable_items, { params });
      this.assignableRows = data.results || [];
      this.assignableCount = data.count || 0;
    },
    isSelected(key) {
      return Boolean(this.selectedMap[key]);
    },
    toggleRow(row) {
      if (this.selectedMap[row.key]) {
        const next = { ...this.selectedMap };
        delete next[row.key];
        this.selectedMap = next;
      } else {
        this.selectedMap = { ...this.selectedMap, [row.key]: { ...row } };
      }
    },
    selectAllFiltered() {
      const next = { ...this.selectedMap };
      this.assignableRows.forEach((row) => {
        next[row.key] = { ...row };
      });
      this.selectedMap = next;
    },
    deselectAll() {
      this.selectedMap = {};
    },
    removeSelected(key) {
      const next = { ...this.selectedMap };
      delete next[key];
      this.selectedMap = next;
    },
    validateClient() {
      this.errors = {};
      const start = new Date(`${this.startDate}T${this.startTime}:00`);
      const end = new Date(`${this.endDate}T${this.endTime}:00`);
      if (!(start < end)) {
        this.errors.non_field_errors = this.$t('start_must_be_before_end');
        return false;
      }
      const value = Number(this.form.discount_value);
      if (this.form.discount_type === 'percentage' && (!(value > 0) || value > 100)) {
        this.errors.discount_value = this.$t('percentage_range_error');
        return false;
      }
      if (this.form.discount_type === 'fixed_amount' && !(value > 0)) {
        this.errors.discount_value = this.$t('fixed_discount_error');
        return false;
      }
      if (this.form.discount_type === 'promotional_price' && value < 0) {
        this.errors.discount_value = this.$t('promo_price_error');
        return false;
      }
      return true;
    },
    toApiDateTime(date, time) {
      // Backend DATETIME_INPUT_FORMATS: YYYY-MM-DD HH:MM:SS (space, not ISO "T").
      const t = (time || '00:00').length === 5 ? `${time}:00` : time;
      return `${date} ${t}`;
    },
    buildPayload() {
      return {
        name: this.form.name,
        description: this.form.description,
        discount_type: this.form.discount_type,
        discount_value: this.form.discount_value,
        max_discount_amount: this.form.discount_type === 'percentage'
          ? (this.form.max_discount_amount || null)
          : null,
        priority: this.form.priority || 0,
        start_at: this.toApiDateTime(this.startDate, this.startTime),
        end_at: this.toApiDateTime(this.endDate, this.endTime),
        store_ids: this.allStores ? [] : this.form.store_ids,
        item_assignments: this.selectedList.map((row) => ({
          item_id: row.item_id,
          variant_id: row.variant_id || null,
        })),
      };
    },
    formatApiErrors(data) {
      if (!data || typeof data !== 'object') return this.$t('something_went_wrong');
      const parts = [];
      Object.keys(data).forEach((k) => {
        const val = data[k];
        const text = Array.isArray(val) ? val.join(' ') : String(val);
        this.errors[k] = text;
        if (k === 'non_field_errors' || k === 'detail') parts.push(text);
        else parts.push(`${k}: ${text}`);
      });
      return parts.join(' ') || this.$t('something_went_wrong');
    },
    async submitForm() {
      if (!this.validateClient()) return;
      this.saving = true;
      this.errors = {};
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await this.$axios.put(apiUrls.discount_campaign.update(this.id), payload);
          this.toast.success(this.$t('campaign_updated'));
          this.$router.push({ name: 'DiscountCampaignDetail', params: { id: this.id } });
        } else {
          const { data } = await this.$axios.post(apiUrls.discount_campaign.post, payload);
          this.toast.success(this.$t('campaign_created'));
          this.$router.push({ name: 'DiscountCampaignDetail', params: { id: data.id } });
        }
      } catch (err) {
        const message = this.formatApiErrors(err?.response?.data);
        this.toast.error(message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
